import { useState } from "react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@mantis-sas/ui";
import { 
  Loader2, 
  Upload, 
  XCircle, 
  CheckCircle2, 
  AlertCircle,
  FileSpreadsheet,
  MapPin,
  Eye,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Info,
  AlertTriangle,
  Package,
  Settings
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { validateBulkInventoryItems, createBulkInventoryItems } from '../../../../ApiCalls/mantisFlowsInventory';

const REQUIRED_COLUMNS = [
];
const OPTIONAL_COLUMNS = [
  { key: "sku", label: "SKU" },
  { key: "unit", label: "Unidad" },
  { key: "name", label: "Nombre" },
  { key: "common_price", label: "Precio" },
  { key: "description", label: "Descripción" },
];

export default function BulkUploadModal({ open, onClose, clientId, customColumns = [], onSuccess }) {
  const [step, setStep] = useState(1); // 1: upload, 2: map, 3: preview, 4: result
  const [file, setFile] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelColumns, setExcelColumns] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [result, setResult] = useState(null);
  const [headerRow, setHeaderRow] = useState(1); // 1-based index
  const [startRow, setStartRow] = useState(2); // Fila donde empiezan los datos
  const [processType, setProcessType] = useState('all'); // 'all', 'alternate', o 'row_and_category'
  const [rawRows, setRawRows] = useState([]); // For previewing raw rows
  const [categoryColumn, setCategoryColumn] = useState(0); // Columna donde está la categoría en el modo fila y categoría

  // Construir columnas requeridas y opcionales del sistema
  const systemColumns = [
    ...REQUIRED_COLUMNS,
    ...OPTIONAL_COLUMNS,
    // Agregar categoría como columna del sistema
    { key: "categoria", label: "Categoría" },
    ...customColumns.map(c => ({ key: c.column_name, label: c.display_name, required: c.is_required }))
  ];
  const requiredKeys = [
    ...REQUIRED_COLUMNS.map(c => c.key),
    ...customColumns.filter(c => c.is_required).map(c => c.column_name)
  ];

  // Detectar si los headers del Excel son inválidos
  const hasInvalidHeaders = excelColumns.some(col => col.startsWith("__EMPTY"));

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[
          { num: 1, label: "Subir", icon: Upload },
          { num: 2, label: "Mapear", icon: MapPin },
          { num: 3, label: "Previsualizar", icon: Eye },
          { num: 4, label: "Completar", icon: CheckCircle }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              step >= s.num 
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 border-orange-500 text-white shadow-lg' 
                : 'border-slate-300 text-slate-400 bg-white'
            }`}>
              {step > s.num ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <s.icon className="h-5 w-5" />
              )}
            </div>
            <span className={`ml-2 text-sm font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-500'}`}>
              {s.label}
            </span>
            {idx < 3 && (
              <ArrowRight className={`h-4 w-4 mx-4 ${step > s.num ? 'text-orange-500' : 'text-slate-300'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  )

  // Paso 1: Subir archivo
  const handleFileChange = (e) => {
    setUploadError("");
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      setRawRows(raw);
      processExcelWithHeaderRow(raw, 1);
    };
    reader.readAsBinaryString(f);
  };

  // Procesar el Excel usando la fila seleccionada como encabezado
  const processExcelWithHeaderRow = (raw, headerRowIdx) => {
    const headers = raw[headerRowIdx - 1] || [];
    let dataRows;
    
    if (processType === 'alternate') {
      // Procesar filas alternadas (items y categorías)
      dataRows = raw.slice(startRow - 1).filter((_, index) => index % 2 === 0);
    } else if (processType === 'row_and_category') {
      // Procesar filas en pares: fila de datos + fila de categoría
      const sourceRows = raw.slice(startRow - 1);
      dataRows = [];
      
      for (let i = 0; i < sourceRows.length; i += 2) {
        const itemRow = sourceRows[i];
        const categoryRow = sourceRows[i + 1];
        
        if (itemRow) {
          // Crear objeto con datos del item
          const itemObj = {};
          headers.forEach((h, idx) => {
            itemObj[h || `__EMPTY_${idx}`] = itemRow[idx];
          });
          
          // Agregar la categoría desde la fila siguiente (si existe)
          if (categoryRow) {
            const categoryValue = categoryRow[categoryColumn] || categoryRow[0]; // Usar columna específica o primera columna
            if (categoryValue && categoryValue.toString().trim() !== '') {
              itemObj['categoria'] = categoryValue.toString().trim();
            }
          }
          
          dataRows.push(itemObj);
        }
      }
    } else {
      // Procesar todas las filas
      dataRows = raw.slice(startRow - 1);
    }

    let data;
    if (processType === 'row_and_category') {
      // Ya procesamos los datos arriba para este tipo
      data = dataRows;
    } else {
      // Procesar normalmente para otros tipos
      data = dataRows.map(row => {
        const obj = {};
        headers.forEach((h, i) => {
          obj[h || `__EMPTY_${i}`] = row[i];
        });
        return obj;
      });
    }

    setExcelData(data);
    setExcelColumns(headers.map((h, i) => h || `__EMPTY_${i}`));
    setHeaderRow(headerRowIdx);
    
    const initialMap = {};
    systemColumns.forEach(sysCol => {
      const found = headers.find(h => h && h.toLowerCase() === sysCol.key.toLowerCase());
      if (found) initialMap[sysCol.key] = found;
    });
    
    // Si estamos en modo fila y categoría, mapear automáticamente la categoría
    if (processType === 'row_and_category') {
      initialMap['categoria'] = 'categoria';
    }
    
    setColumnMap(initialMap);
    setStep(2);
  };

  // Paso 2: Mapeo de columnas
  const handleMapChange = (sysKey, excelKey) => {
    setColumnMap(prev => ({ ...prev, [sysKey]: excelKey }));
  };
  const canContinueMapping = requiredKeys.every(key => columnMap[key] && columnMap[key] !== "");

  // Paso 3: Preview y validación
  const handleValidate = async () => {
    setLoading(true);
    setValidationResult(null);
    setUploadError("");
    try {
      // Mapear los datos del excel a la estructura del sistema
      const mappedData = excelData.map(row => {
        const obj = {};
        systemColumns.forEach(col => {
          if (columnMap[col.key] || col.key === 'categoria') {
            // Para categoria, usar directamente si existe en row, o usar el mapeo
            if (col.key === 'categoria') {
              const value = row['categoria'] || (columnMap[col.key] ? row[columnMap[col.key]] : null);
              obj[col.key] = value === "" || value === undefined ? null : value;
            } else if (columnMap[col.key]) {
              // Convertir valores vacíos o undefined a null
              const value = row[columnMap[col.key]];
              obj[col.key] = value === "" || value === undefined ? null : value;
            }
          }
        });
        return obj;
      }).filter(item => {
        // Filtrar items que tengan al menos un valor no nulo
        return Object.values(item).some(value => value !== null);
      });

      const res = await validateBulkInventoryItems(clientId, mappedData);
      setValidationResult(res);
      setStep(3);
    } catch (err) {
      setUploadError("Error al validar el archivo. Verifica el formato y los datos.");
    } finally {
      setLoading(false);
    }
  };

  // Paso 4: Confirmar y cargar
  const handleUpload = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Mapear los datos del excel a la estructura del sistema
      const mappedData = excelData.map(row => {
        const obj = {};
        systemColumns.forEach(col => {
          if (columnMap[col.key] || col.key === 'categoria') {
            // Para categoria, usar directamente si existe en row, o usar el mapeo
            if (col.key === 'categoria') {
              const value = row['categoria'] || (columnMap[col.key] ? row[columnMap[col.key]] : null);
              obj[col.key] = value === "" || value === undefined ? null : value;
            } else if (columnMap[col.key]) {
              // Convertir valores vacíos o undefined a null
              const value = row[columnMap[col.key]];
              obj[col.key] = value === "" || value === undefined ? null : value;
            }
          }
        });
        return obj;
      }).filter(item => {
        // Filtrar items que tengan al menos un valor no nulo
        return Object.values(item).some(value => value !== null);
      });

      const res = await createBulkInventoryItems(clientId, mappedData);
      setResult(res);
      if (onSuccess) onSuccess();
      setStep(4);
    } catch (err) {
      setUploadError("Error al cargar los datos. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setExcelData([]);
    setExcelColumns([]);
    setColumnMap({});
    setValidationResult(null);
    setResult(null);
    setUploadError("");
    setHeaderRow(1);
    setStartRow(2);
    setProcessType('all');
    setRawRows([]);
    setCategoryColumn(0);
  };

  // Columnas del Excel no mapeadas
  const unusedExcelColumns = excelColumns.filter(ec => !Object.values(columnMap).includes(ec));

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] overflow-hidden bg-gradient-to-br from-slate-50 to-white">
        <DialogHeader className="border-b border-slate-200 pb-6">
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center">
            <Package className="h-6 w-6 text-orange-500 mr-3" />
            Carga Masiva de Inventario
          </DialogTitle>
          <p className="text-slate-600 mt-2">Importa tus productos al inventario con nuestro asistente paso a paso</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <StepIndicator />

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded shadow-sm border border-slate-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Sube tu archivo Excel</h3>
                  <p className="text-slate-600 mb-8">Selecciona un archivo Excel (.xlsx o .xls) con los datos de inventario</p>
                  
                  <div className="relative mb-8">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="file-upload"
                    />
                    <label 
                      htmlFor="file-upload"
                      className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-orange-300 rounded hover:border-orange-400 transition-colors cursor-pointer bg-orange-50/50 hover:bg-orange-50"
                    >
                      <FileSpreadsheet className="h-12 w-12 text-orange-500 mb-4" />
                      <span className="text-lg font-medium text-slate-700">Haz clic para seleccionar archivo</span>
                      <span className="text-sm text-slate-500 mt-1">o arrastra y suelta tu archivo Excel aquí</span>
                    </label>
                  </div>
                  
                  {file && (
                    <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <span className="font-medium text-green-800">{file.name}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Información de formatos soportados */}
                  <div className="bg-slate-50 border border-slate-200 rounded p-6 text-left">
                    <div className="flex items-center mb-4">
                      <Info className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="font-semibold text-slate-700">Formatos de archivo soportados:</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white p-4 rounded border border-slate-200">
                        <div className="font-medium text-slate-600 mb-2 flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          📋 Todas las filas
                        </div>
                        <div className="text-slate-500">Cada fila contiene un item completo con todas sus columnas.</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded border border-slate-200">
                        <div className="font-medium text-slate-600 mb-2 flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          🔄 Filas alternadas
                        </div>
                        <div className="text-slate-500">Procesa cada segunda fila (útil para filtrar datos).</div>
                      </div>
                      
                      <div className="bg-white p-4 rounded border border-slate-200">
                        <div className="font-medium text-slate-600 mb-2 flex items-center">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                          📝 Fila y categoría
                        </div>
                        <div className="text-slate-500">
                          Fila 1: Datos del item<br/>
                          Fila 2: Categoría del item<br/>
                          <span className="text-orange-600 font-medium">✨ Las categorías se crean automáticamente</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {uploadError && (
                    <Alert className="mt-6 border-red-200 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800">{uploadError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Left Column - Configuration */}
              <div className="space-y-6">
                {/* File Configuration */}
                <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <Settings className="h-4 w-4 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Configuración del Archivo</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Fila de encabezados</label>
                      <select
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                        value={headerRow}
                        onChange={e => processExcelWithHeaderRow(rawRows, parseInt(e.target.value))}
                      >
                        {rawRows.slice(0, 10).map((row, idx) => (
                          <option key={idx} value={idx + 1}>
                            Fila {idx + 1}: {row.join(' | ').slice(0, 50)}...
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Fila donde empiezan los datos</label>
                      <select
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                        value={startRow}
                        onChange={e => setStartRow(parseInt(e.target.value))}
                      >
                        {rawRows.slice(0, 10).map((row, idx) => (
                          <option key={idx} value={idx + 1}>
                            Fila {idx + 1}: {row.join(' | ').slice(0, 50)}...
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de procesamiento</label>
                      <select
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                        value={processType}
                        onChange={e => {
                          setProcessType(e.target.value);
                          // Re-procesar cuando cambie el tipo
                          if (rawRows.length > 0) {
                            processExcelWithHeaderRow(rawRows, headerRow);
                          }
                        }}
                      >
                        <option value="all">Procesar todas las filas</option>
                        <option value="alternate">Procesar filas alternadas</option>
                        <option value="row_and_category">Fila y categoría (item + categoría abajo)</option>
                      </select>
                    </div>
                    
                    {processType === 'row_and_category' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Columna donde está la categoría</label>
                        <select
                          className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                          value={categoryColumn}
                          onChange={e => {
                            setCategoryColumn(parseInt(e.target.value));
                            // Re-procesar cuando cambie la columna de categoría
                            if (rawRows.length > 0) {
                              processExcelWithHeaderRow(rawRows, headerRow);
                            }
                          }}
                        >
                          {rawRows[0] && rawRows[0].map((_, idx) => (
                            <option key={idx} value={idx}>Columna {idx + 1}</option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500 mt-2">
                          Selecciona en qué columna de la fila de categoría se encuentra el nombre de la categoría
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Invalid Headers Warning */}
                {hasInvalidHeaders && (
                  <Alert className="border-yellow-200 bg-yellow-50">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      El archivo Excel no tiene encabezados válidos. Selecciona la fila que contiene los nombres de las columnas reales.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Processing Type Info */}
                {processType === 'row_and_category' && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <div className="font-medium mb-2">Modo: Fila y Categoría</div>
                      <ul className="text-sm space-y-1">
                        <li>• <strong>Filas impares:</strong> Datos del item (nombre, SKU, stock, etc.)</li>
                        <li>• <strong>Filas pares:</strong> Categoría del item (columna {categoryColumn + 1})</li>
                        <li>• Los datos se combinan automáticamente</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Column Mapping */}
                <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center mr-3">
                      <MapPin className="h-4 w-4 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Mapear Columnas del Sistema</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {systemColumns.map(col => (
                      <div key={col.key} className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-slate-700">
                          {col.label}
                          {requiredKeys.includes(col.key) && (
                            <Badge variant="destructive" className="ml-2 text-xs">Requerido</Badge>
                          )}
                        </label>
                        <select
                          className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                          value={columnMap[col.key] || ""}
                          onChange={e => handleMapChange(col.key, e.target.value)}
                        >
                          <option value="">Selecciona columna...</option>
                          {excelColumns.map(ec => (
                            <option key={ec} value={ec}>{ec}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={reset} className="flex items-center">
                    <Upload className="h-4 w-4 mr-2" />
                    Subir otro archivo
                  </Button>
                  <Button variant="outline" onClick={() => setStep(1)} className="flex items-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Atrás
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white flex items-center"
                    onClick={() => setStep(3)}
                    disabled={!canContinueMapping}
                  >
                    Siguiente: Previsualizar
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>

                {/* Unmapped Columns */}
                {unusedExcelColumns.length > 0 && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <span className="font-medium">Columnas del Excel no mapeadas:</span><br />
                      {unusedExcelColumns.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Right Column - Preview */}
              <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                    <Eye className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Vista Previa de Datos</h3>
                </div>
                
                <div className="overflow-hidden rounded border border-slate-200">
                  <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          {processType === 'row_and_category' ? (
                            // Mostrar columnas del sistema para fila y categoría
                            systemColumns.filter(col => columnMap[col.key] || col.key === 'categoria').map(col => (
                              <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                {col.label}
                              </th>
                            ))
                          ) : (
                            // Mostrar columnas originales del Excel
                            excelColumns.map(col => (
                              <th key={col} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                {col}
                              </th>
                            ))
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {processType === 'row_and_category' ? (
                          // Mostrar datos procesados para fila y categoría
                          excelData.slice(0, 10).map((row, i) => (
                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                              {systemColumns.filter(col => columnMap[col.key] || col.key === 'categoria').map(col => (
                                <td key={col.key} className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                                  {col.key === 'categoria' ? row['categoria'] || '-' : row[columnMap[col.key]] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : (
                          // Mostrar datos originales del Excel
                          excelData.slice(0, 10).map((row, i) => (
                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                              {excelColumns.map(col => (
                                <td key={col} className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                                  {row[col] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {excelData.length > 10 && (
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    Mostrando primeras 10 filas de {excelData.length} filas totales
                  </p>
                )}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Revisar Datos Mapeados</h3>
                  <div className="flex flex-wrap gap-2">
                    {systemColumns.map(col => (
                      <Badge
                        key={col.key}
                        variant={(columnMap[col.key] || col.key === 'categoria') ? "default" : "secondary"}
                        className={(columnMap[col.key] || col.key === 'categoria') ? "bg-green-100 text-green-800 border-green-200" : ""}
                      >
                        {col.label}
                        {col.key === 'categoria' && processType === 'row_and_category' && ' (auto)'}
                        {(columnMap[col.key] || col.key === 'categoria') && <CheckCircle className="h-3 w-3 ml-1" />}
                      </Badge>
                    ))}
                  </div>
                  
                  {processType === 'row_and_category' && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-4">
                      <div className="flex items-center mb-3">
                        <Info className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="font-medium text-blue-800">Vista previa de datos procesados (Fila y Categoría)</span>
                      </div>
                      <div className="overflow-hidden rounded border border-blue-300">
                        <div className="overflow-x-auto max-h-64">
                          <table className="min-w-full divide-y divide-blue-200">
                            <thead className="bg-blue-100">
                              <tr>
                                {systemColumns.filter(col => columnMap[col.key] || col.key === 'categoria').map(col => (
                                  <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-blue-200">
                              {excelData.slice(0, 5).map((row, i) => (
                                <tr key={i} className="hover:bg-blue-50 transition-colors">
                                  {systemColumns.filter(col => columnMap[col.key] || col.key === 'categoria').map(col => (
                                    <td key={col.key} className="px-3 py-2 text-xs text-slate-900">
                                      {col.key === 'categoria' ? 
                                        (row['categoria'] || 'Sin categoría') : 
                                        (row[columnMap[col.key]] || '-')
                                      }
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        Mostrando los primeros 5 items procesados con sus categorías asignadas
                      </p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleValidate} 
                  disabled={loading || excelData.length === 0} 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Validar Datos
                </Button>
              </div>

              {/* Validation Results */}
              {validationResult && (
                <div className="space-y-6">
                  {/* Status Header */}
                  <div className={`rounded p-6 border ${
                    validationResult.canProceed && validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {validationResult.canProceed && validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0 ? (
                        <CheckCircle className="h-8 w-8 text-green-600 mr-4" />
                      ) : (
                        <XCircle className="h-8 w-8 text-red-600 mr-4" />
                      )}
                      <div>
                        <h4 className={`text-lg font-semibold ${
                          validationResult.canProceed && validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0 
                            ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {validationResult.canProceed && validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0 
                            ? 'Validación Exitosa!' : 'Problemas de Validación Encontrados'}
                        </h4>
                        <p className={`text-sm ${
                          validationResult.canProceed && validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0 
                            ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {validationResult.message}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Errors List */}
                  {validationResult.errors && validationResult.errors.length > 0 && (
                    <div className="bg-white rounded shadow-sm border border-red-200 p-6">
                      <h5 className="font-semibold text-red-900 mb-3 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        Errores de Validación
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {validationResult.errors.map((err, idx) => (
                          <div key={idx} className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
                            {err}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Valid Items */}
                    <div className="bg-white rounded shadow-sm border border-green-200 p-6">
                      <h5 className="font-semibold text-green-900 mb-3 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Items Válidos ({validationResult.data?.valid?.length || 0})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 0 ? (
                          validationResult.data.valid.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
                              {Object.values(item).join(' | ')}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-400 text-sm">No hay items válidos para cargar</p>
                        )}
                        {validationResult.data && validationResult.data.valid && validationResult.data.valid.length > 5 && (
                          <p className="text-slate-400 text-xs">...y {validationResult.data.valid.length - 5} más</p>
                        )}
                      </div>
                    </div>

                    {/* Invalid Items */}
                    <div className="bg-white rounded shadow-sm border border-red-200 p-6">
                      <h5 className="font-semibold text-red-900 mb-3 flex items-center">
                        <XCircle className="h-5 w-5 mr-2" />
                        Items Inválidos ({validationResult.data?.invalid?.length || 0})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {validationResult.data && validationResult.data.invalid && validationResult.data.invalid.length > 0 ? (
                          validationResult.data.invalid.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
                              {Object.values(item.item || item).join(' | ')}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-400 text-sm">Ningún item inválido</p>
                        )}
                        {validationResult.data && validationResult.data.invalid && validationResult.data.invalid.length > 5 && (
                          <p className="text-slate-400 text-xs">...y {validationResult.data.invalid.length - 5} más</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleUpload}
                      className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Confirmar y Cargar ({validationResult.data?.valid?.length || 0} items)
                    </Button>
                    
                    <Button variant="outline" onClick={reset} className="flex items-center">
                      <Upload className="h-4 w-4 mr-2" />
                      Subir otro archivo
                    </Button>
                    
                    <Button variant="outline" onClick={() => setStep(2)} className="flex items-center">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Atrás
                    </Button>
                  </div>
                </div>
              )}

              {!validationResult && (
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} className="flex items-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Atrás
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && result && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                  result.errors && result.errors.length > 0 
                    ? 'bg-gradient-to-r from-red-500 to-red-600' 
                    : 'bg-gradient-to-r from-green-500 to-green-600'
                }`}>
                  {result.errors && result.errors.length > 0 ? (
                    <XCircle className="h-8 w-8 text-white" />
                  ) : (
                    <CheckCircle className="h-8 w-8 text-white" />
                  )}
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {result.errors && result.errors.length > 0 ? 'Carga Completada con Errores' : 'Carga Completada Exitosamente!'}
                </h3>
                
                <p className={`mb-6 ${result.errors && result.errors.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  {result.errors && result.errors.length > 0 
                    ? 'Algunos items no se pudieron cargar'
                    : 'Todos los items se cargaron correctamente'
                  }
                </p>

                {result.errors && result.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-4 mb-6 text-left">
                    <h5 className="font-medium text-red-900 mb-2">Errores de Carga:</h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {result.errors.map((err, idx) => (
                        <div key={idx} className="text-sm text-red-700">
                          Fila {err.row + 1}: {err.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-3">
                  <Button onClick={reset} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                    <Upload className="h-4 w-4 mr-2" />
                    Cargar otro archivo
                  </Button>
                  <Button variant="outline" onClick={() => { reset(); onClose(); }}>
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 pt-6">
          <Button variant="outline" onClick={() => { reset(); onClose(); }} className="border-slate-300">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 